
import { Component, Mixins, Prop, Provide } from "vue-property-decorator";
import { mapState } from "vuex";
import {
  FilteringSortingTableStore,
  IFilter
} from "@/mixins/tableWithFilteringAndSortingStore";
import { DataModules } from "@/store/modules/data";
import { IBrand } from "@/models/brands";
import { CreateElement } from "vue";
import {
  IBrandsProvider,
  IBrandsProviderMethods
} from "@/models/providers/brands";
import { ISorter } from "@/models/tables";
import { DefaultBrandFilters } from "@/data/filters/brand";
import { DefaultBrandSorters } from "@/data/sorters/brand";

@Component({
  computed: {
    ...mapState({
      currentBrandId: (state: any) => state.auth.admin.brandId
    })
  }
})
export default class BrandsProvider extends Mixins(FilteringSortingTableStore) {
  // Props

  @Prop({ default: "BP" /** Brands Provider */ })
  public declare id: string;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => DefaultBrandFilters })
  public availableFilters!: (vm: BrandsProvider) => IFilter[];

  @Prop({ default: () => DefaultBrandSorters })
  public availableSorters!: (vm: BrandsProvider) => ISorter[];

  // Data
  public readonly currentBrandId!: IBrand["id"];
  public dataModule: string = DataModules.BRANDS;

  public filters = this.brandFilters;
  public sorters = this.brandSorters;

  // Life-cycle methods

  public created() {
    this.loadData();
  }

  // Computed

  public get brandFilters() {
    return this.availableFilters(this);
  }

  public get brandSorters() {
    return this.availableSorters(this);
  }

  public get canList() {
    return this.$userCan("list_brands");
  }

  public get canCreate() {
    return this.$userCan("create_brand");
  }

  public get canUpdate() {
    return this.$userCan("update_brand");
  }

  public get canDelete() {
    return this.$userCan("delete_brand");
  }

  public get guardFunctionalities() {
    return ["list_brands"];
  }

  // Methods

  public loadData() {
    if (!this.canList) return;
    this.getData();
  }

  public getStoreParams(payload: any): Promise<any> {
    return this.$_.merge(
      {
        params: {
          with: ["image"].join()
        }
      },
      payload // Important – must merge last
    );
  }

  public reloadData() {
    this.cancelRequests();
    return this.getData({ ignoreStored: true });
  }

  public async openDeleteBrandConfirmModal(brandId: IBrand["id"]) {
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.brand") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            try {
              await this.deleteBrand(brandId);
              this.reloadData();
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              confirm.close();
            }
          }
        }
      }
    });
  }

  public async deleteBrand(brandId: IBrand["id"]) {
    return this.$store.dispatch("data/brands/remove", brandId);
  }

  public manageBrand(id: IBrand["id"], name: string): void {
    this.$router
      .push({
        name: "adminBrand",
        params: {
          id,
          name,
          redirect: "/admin/settings/general"
        }
      })
      // https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
      .catch(() => ({}));
  }

  // Provide

  @Provide("brandsProviderMethods")
  public get providerMethods(): IBrandsProviderMethods {
    return {
      getData: this.getData,
      manageBrand: this.manageBrand,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      openDeleteBrandConfirmModal: this.openDeleteBrandConfirmModal,
      reloadData: this.reloadData
    };
  }

  @Provide("brandsProviderData")
  public providerData(): IBrandsProvider {
    return {
      activeFilters: this.activeFilters,
      brands: this.table.data as IBrand[],
      canDelete: this.canDelete,
      canUpdate: this.canUpdate,
      constQueryFilters: this.constQueryFilters,
      currentBrandId: this.currentBrandId,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      filters: this.filters,
      guardFunctionalities: this.guardFunctionalities,
      isLoading: this.table.loading,
      limit: this.limit,
      page: this.table.page,
      sorters: this.sorters,
      table: this.table,
      total: this.total
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.loadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $brandsData: this.providerData(),
        $brandsMethods: this.providerMethods
      })
    );
  }
}
